import React, { useCallback, useEffect } from 'react'
import { clearClientName, saveClientName } from '../utils/clientIdentity'
import { slugFromPath, showSlugFromPath } from '../utils/links'
import { setStaffPreview } from '../utils/previewMode'
import { ensureAnonSession, supabaseConfigured } from '../lib/supabase'
import { closeProjectSession, openProjectSession } from '../utils/sharedReview'
import { closeShowSession, openShowSession } from '../utils/showSession'
import { fetchProjectById, fetchProjectBySlug, fetchProjects } from '../utils/projectsSource'
import { fetchShowById, fetchShowBySlug } from '../utils/showsSource'
import type { StaffSession } from '../utils/staffAuth'
import type { Activity, Project, Show } from '../types/portal'
import { PREVIEW_PARAM } from './portalPaths'
import { uid } from './portalHelpers'
import type { ClientLoad, ClientRoute, PortalState, ViewMode } from './portalTypes'

/**
 * The client side of the Portal provider: loading the one project (or show)
 * a client link grants, Preview as Client, and the client-session actions.
 *
 * `usePortalClient` is a real hook, called by `PortalProvider` at exactly the
 * point in its hook sequence where this code used to sit inline, so React
 * sees the same hooks in the same order with the same dependency arrays.
 *
 * `buildClientActions` is deliberately NOT a hook: it is called inside the
 * provider's existing `useMemo`, so the context value keeps its single memo
 * boundary and the same dependency array it always had.
 */

interface PortalClientInput {
  path: string
  search: string
  staffSession: StaffSession | null
  authed: boolean
  clientName: string
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>
  setActiveProjectId: React.Dispatch<React.SetStateAction<string>>
  setClientLoad: React.Dispatch<React.SetStateAction<ClientLoad>>
  setActiveShowId: React.Dispatch<React.SetStateAction<string>>
  setShows: React.Dispatch<React.SetStateAction<Show[]>>
  setClientRoute: React.Dispatch<React.SetStateAction<ClientRoute>>
}

export function usePortalClient({
  path,
  search,
  staffSession,
  authed,
  clientName,
  setProjects,
  setActiveProjectId,
  setClientLoad,
  setActiveShowId,
  setShows,
  setClientRoute,
}: PortalClientInput) {
  /**
   * The client side never lists projects — it loads the one its link grants
   * access to, by the real id join_project returns.
   */
  /** Takes a stored project into state and puts it on screen. */
  const adoptClientProject = useCallback((project: Project) => {
    setProjects((list) => {
      const held = list.find((item) => item.slug === project.slug)
      if (!held) return [project, ...list]
      return list.map((item) =>
        item.slug === project.slug
          ? {
              ...held,
              ...project,
              deliverables: project.deliverables.map((entry) => {
                const local = held.deliverables.find((row) => row.id === entry.id)
                return local ? { ...local, ...entry } : entry
              }),
            }
          : item,
      )
    })
    setActiveProjectId(project.id)
  }, [])

  const loadClientProject = useCallback(
    async (id: string) => {
      const project = await fetchProjectById(id)
      if (!project) return false
      adoptClientProject(project)
      return true
    },
    [adoptClientProject],
  )

  /** Same idea as adoptClientProject, for the several projects a show link grants at once. */
  const adoptClientProjects = useCallback((rows: Project[]) => {
    setProjects((list) => {
      const held = new Map(list.map((item) => [item.slug, item]))
      const merged = rows.map((project) => {
        const existing = held.get(project.slug)
        if (!existing) return project
        return {
          ...existing,
          ...project,
          deliverables: project.deliverables.map((entry) => {
            const local = existing.deliverables.find((row) => row.id === entry.id)
            return local ? { ...local, ...entry } : entry
          }),
        }
      })
      const untouched = list.filter((item) => !rows.some((row) => row.slug === item.slug))
      return [...merged, ...untouched]
    })
  }, [])

  /**
   * Whether this browser is a verified staff member looking at the client
   * portal. The flag alone proves nothing — without a staff session a
   * ?preview=1 link is simply an ordinary client link.
   */
  const previewRequested =
    Boolean(slugFromPath(path)) && new URLSearchParams(search).get(PREVIEW_PARAM) === '1'
  const clientPreview = Boolean(staffSession) && previewRequested

  /* Decided once, here, and handed to the data layer so the shared-review
     helpers withhold join_project too. */
  useEffect(() => {
    setStaffPreview(clientPreview)
  }, [clientPreview])

  /**
   * The one bootstrap behind a client link, for both audiences.
   *
   * A client exchanges the link's slug and access token for their project.
   * Staff previewing it read the same stored project straight off its slug —
   * has_project_access() already recognises Portal staff — so no name is
   * asked for, join_project is never called, and no project_members row is
   * created. Either way the outcome is stated: loading, ready, or why not.
   */
  useEffect(() => {
    const projectSlug = slugFromPath(path)
    const showSlug = projectSlug ? '' : showSlugFromPath(path)

    /* A show link grants every project inside it at once, rather than one
       project the way a project link does — same bootstrap shape, one level
       up, sharing clientLoad so ClientApp's loading/error screens need no
       changes. */
    if (showSlug) {
      if (!supabaseConfigured) {
        setClientLoad('idle')
        return
      }

      let live = true
      setClientLoad('loading')

      const settle = async (show: Show | null) => {
        if (!live) return
        if (!show) {
          setClientLoad('missing')
          return
        }
        setActiveShowId(show.id)
        setShows((list) => (list.some((item) => item.id === show.id) ? list : [show, ...list]))
        const rows = await fetchProjects()
        if (!live) return
        const episodes = rows.filter((item) => item.showId === show.id)
        adoptClientProjects(episodes)
        /* A show with only one episode so far has nowhere useful to route to
           first — a landing page listing exactly one thing is a click a
           client didn't need. Go straight into that episode, the same
           experience an ordinary project link already gives, and let the
           show landing page only exist once there is a real list on it. */
        if (episodes.length === 1) {
          setActiveProjectId(episodes[0].id)
          setClientRoute('home')
        } else {
          setClientRoute('show')
        }
        setClientLoad('ready')
      }

      if (clientPreview) {
        void fetchShowBySlug(showSlug)
          .then(settle)
          .catch((error: unknown) => {
            if (!live) return
            console.error('[preview] show load failed', error)
            setClientLoad('failed')
          })
        return () => {
          live = false
        }
      }

      if (!authed) {
        setClientLoad('idle')
        return
      }

      void ensureAnonSession()
        .then(() => openShowSession(showSlug, clientName || 'Client'))
        .then(async (id) => {
          if (!live) return
          if (!id) {
            setClientLoad('denied')
            return
          }
          await settle(await fetchShowById(id))
        })
        .catch((error: unknown) => {
          if (!live) return
          console.error('[client] show load failed', error)
          setClientLoad('failed')
        })

      return () => {
        live = false
      }
    }

    if (!projectSlug) {
      setClientLoad('idle')
      return
    }
    /* Without Supabase there is nothing to read: the prototype's own content
       stands in, rather than reporting a failure the viewer cannot act on. */
    if (!supabaseConfigured) {
      setClientLoad('idle')
      return
    }

    let live = true
    setClientLoad('loading')

    if (clientPreview) {
      void fetchProjectBySlug(projectSlug)
        .then((project) => {
          if (!live) return
          if (!project) {
            setClientLoad('missing')
            return
          }
          adoptClientProject(project)
          setClientLoad('ready')
        })
        .catch((error: unknown) => {
          if (!live) return
          console.error('[preview] project load failed', error)
          setClientLoad('failed')
        })
      return () => {
        live = false
      }
    }

    if (!authed) {
      setClientLoad('idle')
      return
    }

    void ensureAnonSession()
      .then(() => openProjectSession(projectSlug, clientName || 'Client'))
      .then(async (id) => {
        if (!live) return
        if (!id) {
          setClientLoad('denied')
          return
        }
        const ok = await loadClientProject(id)
        if (!live) return
        setClientLoad(ok ? 'ready' : 'missing')
      })
      .catch((error: unknown) => {
        if (!live) return
        console.error('[client] project load failed', error)
        setClientLoad('failed')
      })

    return () => {
      live = false
    }
  }, [
    authed,
    clientPreview,
    path,
    clientName,
    loadClientProject,
    adoptClientProject,
    adoptClientProjects,
  ])

  return { loadClientProject, previewRequested, clientPreview }
}

interface ClientActionInput {
  authed: boolean
  clientName: string
  path: string
  activeProject: Project
  projectId: string
  viewerName: string
  clientPreview: boolean
  previewRequested: boolean
  clientLoad: ClientLoad
  setClientName: React.Dispatch<React.SetStateAction<string>>
  setAuthed: React.Dispatch<React.SetStateAction<boolean>>
  setViewModeState: React.Dispatch<React.SetStateAction<ViewMode>>
  setClientRoute: React.Dispatch<React.SetStateAction<ClientRoute>>
  setActiveShowId: React.Dispatch<React.SetStateAction<string>>
  setActivity: React.Dispatch<React.SetStateAction<Activity[]>>
  loadClientProject: (id: string) => Promise<boolean>
}

export type ClientActions = Pick<
  PortalState,
  | 'authed'
  | 'clientName'
  | 'signIn'
  | 'changeClientName'
  | 'contactPortal'
  | 'canComment'
  | 'canApprove'
  | 'canEditTranscripts'
  | 'clientPreview'
  | 'previewRequested'
  | 'clientLoad'
>

export function buildClientActions({
  authed,
  clientName,
  path,
  activeProject,
  projectId,
  viewerName,
  clientPreview,
  previewRequested,
  clientLoad,
  setClientName,
  setAuthed,
  setViewModeState,
  setClientRoute,
  setActiveShowId,
  setActivity,
  loadClientProject,
}: ClientActionInput): ClientActions {
  return {
    authed,
    clientName,
    signIn: (name: string) => {
      const trimmed = name.trim()
      if (!trimmed) return
      saveClientName(trimmed)
      setClientName(trimmed)
      /* Establishes the anonymous session and exchanges the slug + access
         token on this link for the project. The client never sees a login. */
      void ensureAnonSession()
        .then(() => openProjectSession(slugFromPath(path) || activeProject.slug, trimmed))
        .then((id) => (id ? loadClientProject(id) : null))
      setAuthed(true)
      setViewModeState('client')
      setClientRoute('home')
    },
    changeClientName: () => {
      /* A preview has no client identity to change. */
      if (clientPreview) return
      closeProjectSession()
      closeShowSession()
      clearClientName()
      setClientName('')
      setAuthed(false)
      setActiveShowId('')
      setClientRoute('home')
    },

    contactPortal: (body) => {
      if (clientPreview) return
      const who = viewerName
      setActivity((list) => [
        {
          id: uid('act'),
          projectId,
          projectName: activeProject.client,
          actorName: who,
          action: 'sent a message to Portal',
          message: body,
          deliverableId: '',
          at: Date.now(),
        },
        ...list,
      ])
    },

    /**
     * One shared link: everyone who has it can comment and approve.
     *
     * Preview as Client is the same screens reading the same stored data,
     * with every action withheld — staff are looking, not acting on the
     * client's behalf.
     */
    canComment: !clientPreview,
    canApprove: !clientPreview,
    canEditTranscripts: !clientPreview,
    clientPreview,
    previewRequested,
    clientLoad,
  }
}
